import React, { useEffect, useRef, useState } from 'react';
import { webLoadingFalse, webNavigate } from '../../services/navigate.service';
import {
  CUSTOMER_ORDER_SOURCE,
  CUSTOMER_ORDER_STATUS,
  CustomerOrderRow,
  customerOrderSourceLabel,
  customerOrderStatusLabel,
  getListCustomerOrders,
  updateCustomerOrderStatus,
} from '../../services/customerOrders.service';
import { getLocations } from '../../services/inventoryCorrections.service';
import { useResponsive } from '../../hook/useResponsive';
import { showApiError } from '../../services/apiError';
import {
  ListColumn,
  ListFilter,
  ListQuery,
  ListRowAction,
  ListScreen,
  ListScreenHandle,
  cells,
} from '../../components/list';

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const CustomerOrders = () => {
  const [language, setLanguage] = useState<any>(null);
  const { isMobile } = useResponsive();
  const listRef = useRef<ListScreenHandle>(null);
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const [locations, setLocations] = useState<any[]>([]);

  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const response: any = await getLocations(userId, companyId, 1, 100, '');
        setLocations(
          asArray(response?.data?.data?.locations ?? response?.data?.data),
        );
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadLocations();
  }, []);

  const t = (key: string) =>
    language && language?.[key] ? language?.[key] : key;

  const inforTable: any = {
    title: t('No customer orders yet'),
    path: '/customerOrders/',
    desc: t('Create your first customer order'),
  };

  const fetchOrders = async (query: ListQuery) => {
    webLoadingFalse();
    const response: any = await getListCustomerOrders({
      page: query.page,
      limit: query.limit,
      search: query.search || undefined,
      status: query.filters.status ? Number(query.filters.status) : undefined,
      source: query.filters.source ? Number(query.filters.source) : undefined,
      locationId: query.filters.locationId || undefined,
    });
    return {
      rows: response?.data?.data?.rows ?? [],
      count: response?.data?.data?.count ?? 0,
    };
  };

  const quickStatus = async (row: CustomerOrderRow, status: number) => {
    try {
      await updateCustomerOrderStatus(row.id, { status });
      // Swap in place: refetch keeps page/scroll, matching the reservations pattern.
      listRef.current?.reload();
    } catch (error) {
      console.error('Error updating order status:', error);
      showApiError(error, { language });
    }
  };

  const columns: ListColumn<CustomerOrderRow>[] = [
    {
      key: 'reference',
      label: 'Order Reference',
      width: 170,
      hideable: false,
      render: cells.primary(
        (o) => o?.reference ?? ' - ',
        (o) => o?.displayRef ?? undefined,
      ),
      value: (o) => o?.reference ?? ' - ',
      mobile: 'title',
      onClick: (o) => webNavigate(`/customerOrders/${o.id}`),
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      width: 200,
      value: (o) => o?.customerName ?? '',
      mobile: 'subtitle',
    },
    {
      key: 'requestedFor',
      label: 'Requested For',
      width: 160,
      sortable: true,
      sortKey: 'createdAt',
      render: cells.date((o) => o?.requestedFor ?? o?.placedAt),
      value: (o) => o?.requestedFor ?? o?.placedAt ?? '',
      mobile: 'subtitle',
    },
    {
      key: 'source',
      label: 'Source',
      width: 130,
      render: cells.typePill((o) => t(customerOrderSourceLabel(o?.source))),
      value: (o) => customerOrderSourceLabel(o?.source),
    },
    {
      key: 'status',
      label: 'Status',
      width: 130,
      render: cells.status(
        (o) => o?.status === CUSTOMER_ORDER_STATUS.COMPLETED,
        {
          on: 'Completed',
          off: 'Pending',
        },
      ),
      value: (o) => customerOrderStatusLabel(o?.status),
    },
    {
      key: 'amount',
      label: 'Order Total',
      width: 130,
      align: 'right',
      render: cells.money((o) => Number(o?.amount ?? 0), true),
      mobile: 'trailing',
    },
  ];

  const filters: ListFilter[] = [
    {
      key: 'status',
      label: 'Status',
      kind: 'select',
      options: [
        { value: String(CUSTOMER_ORDER_STATUS.PENDING), label: t('Pending') },
        {
          value: String(CUSTOMER_ORDER_STATUS.COMPLETED),
          label: t('Completed'),
        },
        {
          value: String(CUSTOMER_ORDER_STATUS.CANCELLED),
          label: t('Cancelled'),
        },
      ],
    },
    {
      key: 'source',
      label: 'Source',
      kind: 'select',
      options: Object.values(CUSTOMER_ORDER_SOURCE).map((source) => ({
        value: String(source),
        label: t(customerOrderSourceLabel(source)),
      })),
    },
    {
      key: 'locationId',
      label: 'Location',
      kind: 'select',
      options: locations.map((location: any) => ({
        value: location?.id ?? '',
        label: location?.name ?? '',
      })),
    },
  ];

  const rowActions: ListRowAction<CustomerOrderRow>[] = [
    {
      key: 'complete',
      label: 'Mark Completed',
      sheetIcon: 'chev',
      onRun: (o) => quickStatus(o, CUSTOMER_ORDER_STATUS.COMPLETED),
    },
    {
      key: 'cancel',
      label: 'Cancel Order',
      sheetIcon: 'chev',
      // The reason belongs to the confirm flow on the detail screen.
      onRun: (o) => webNavigate(`/customerOrders/${o.id}`),
    },
  ];

  return (
    <div
      className="h-full relative"
      style={{
        overflowY: isMobile ? 'auto' : undefined,
        paddingBottom: isMobile ? '24px' : undefined,
        display: isMobile ? undefined : 'flex',
        flexDirection: isMobile ? undefined : 'column',
      }}
    >
      <div className="lg:flex items-center justify-between">
        <p
          className="font-inter"
          style={{ fontSize: '24px', fontWeight: '500', color: '#222525' }}
        >
          {t('Customer Orders')}
        </p>
        <button
          className="font-inter"
          style={{
            background: '#0E5566',
            color: '#FFFFFF',
            borderRadius: '10px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 500,
            marginTop: isMobile ? '12px' : undefined,
          }}
          onClick={() => webNavigate('/customerOrders/new')}
        >
          {t('New Customer Order')}
        </button>
      </div>
      <ListScreen
        ref={listRef}
        language={language}
        columns={columns}
        fetchRows={fetchOrders}
        searchPlaceholder="Search reference, customer.."
        filters={filters}
        defaultSort={{ sortBy: 'createdAt', sortDir: 'DESC' }}
        rowActions={rowActions}
        countNoun="orders"
        emptyState={inforTable}
      />
    </div>
  );
};

export default CustomerOrders;
